import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import moment from "moment";
import { tripData } from "store/tripSlice";
import {
  tripTimeline,
  vehicleTripData,
  getVehicleTripData,
  timeZone as timeZoneSelector,
} from "store/vehicleTripSlice";
import { GetTimeZoneFromAbbreviation } from "common/Constants";
import configProvider from "config/configProvider";
import pointsService from "services/pointsService";
import pinIcon from "assets/images/red-map-pin-60.png";
import chevronIcon from "assets/images/chevron-blue-24.png";
import markerIcon from "assets/images/marker-48.png";

mapboxgl.accessToken = configProvider.MapboxApiKey;

// OTP Verification: a focused map view showing the pickup-area picture used to
// confirm on-time performance. Shows the dispatcher-ordered pickup point, the
// driver-recorded "Arrived at Pickup" event, and headings within a 1500m radius
// around the pickup over a contractual-pickup ± 1 hour window. Optional toggle
// to render every other event marker.
const OtpVerification: React.FC = () => {
  const dispatch = useDispatch();
  const trip = useSelector(tripData);
  const timeline = useSelector(tripTimeline);
  const activity = useSelector(vehicleTripData);
  const tz = useSelector(timeZoneSelector);

  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [pickupCoords, setPickupCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const SOURCE_PREFIX = "otp";
  const RADIUS_METERS = 750; // 1500m diameter circle drawn around the ordered pickup
  const ACTIVITY_RADIUS_METERS = 1600; // GPS activity filtered to within this radius
                                       // of the driver's actual arrival event
  const WINDOW_BEFORE_HOURS = 1; // show data starting 1h before contractual pickup
  const FALLBACK_AFTER_HOURS = 1; // if no Arrived-at-Pickup, end window contractual+1h

  // Look up the ordered pickup point coordinates via the points service. The trip
  // model only carries point ids; lat/lng lives on the Point record.
  useEffect(() => {
    const pickupPointId = (trip as any)?.pickupLocation?.id;
    if (!pickupPointId) {
      setPickupCoords(null);
      return;
    }
    let cancelled = false;
    pointsService.getPoint(pickupPointId).then((p: any) => {
      if (cancelled) return;
      if (p?.latitude != null && p?.longitude != null) {
        setPickupCoords({ lat: p.latitude, lng: p.longitude });
      } else {
        setPickupCoords(null);
      }
    }).catch(() => { if (!cancelled) setPickupCoords(null); });
    return () => { cancelled = true; };
  }, [trip?.id]);

  // Find the driver-recorded Arrived at Pickup event (if present). Drives the
  // upper bound of the verification window.
  const arrivedEvent = (timeline || []).find((e: any) =>
    e?.type === "Arrived at Pickup" && !e?.isSuggested && e?.isActive !== false
  );

  // Pre-fetch vehicle activity for the verification window when the trip loads.
  // Reuses the same Redux store the Map tab uses; if the user has already opened
  // Map / Closer it'll be cached.
  useEffect(() => {
    if (!trip?.vehicle?.unitNumber) return;
    const contractual = (trip as any)?.contractualPickupTime || trip?.pickupDateTimeLocal;
    if (!contractual) return;
    const start = moment.utc(contractual).subtract(WINDOW_BEFORE_HOURS, "hours");
    const end = arrivedEvent?.time
      ? moment.utc(arrivedEvent.time)
      : moment.utc(contractual).add(FALLBACK_AFTER_HOURS, "hours");
    dispatch(getVehicleTripData({
      unitNumber: +trip.vehicle.unitNumber,
      startDate: start,
      endDate: end,
      timezone: tz,
      tripNumber: String(trip.tripNumber),
      clientName: trip.clientName,
    } as any));
  }, [trip?.id, trip?.vehicle?.unitNumber, arrivedEvent?.time, tz, dispatch]);

  // Initialize map once
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      // Use the same style + flat (mercator) projection as the Map tab. v12 defaults
      // to a globe projection in mapbox-gl 3.x which looked odd at low zoom.
      style: "mapbox://styles/mapbox/streets-v11",
      projection: { name: "mercator" } as any,
      // Start showing the continental US — flyTo() below recenters to the actual
      // pickup once coords resolve.
      center: [-95, 39],
      zoom: 4,
    });
    mapRef.current = map;
    map.on("load", () => {
      // Register chevron + pin + blue-marker images for the heading + pickup + arrived layers.
      const chevronImg = new Image();
      chevronImg.onload = () => { if (!map.hasImage("otp-chevron")) map.addImage("otp-chevron", chevronImg, { sdf: true }); };
      chevronImg.src = chevronIcon;
      chevronImg.width = 25; chevronImg.height = 25;
      const pinImg = new Image();
      pinImg.onload = () => { if (!map.hasImage("otp-pin")) map.addImage("otp-pin", pinImg); };
      pinImg.src = pinIcon;
      pinImg.width = 40; pinImg.height = 40;
      const arrivedImg = new Image();
      arrivedImg.onload = () => { if (!map.hasImage("otp-marker")) map.addImage("otp-marker", arrivedImg); };
      arrivedImg.src = markerIcon;
      arrivedImg.width = 33; arrivedImg.height = 32;
      // Sources + layers
      const emptyFC: any = { type: "FeatureCollection", features: [] };
      map.addSource(`${SOURCE_PREFIX}-circle`, { type: "geojson", data: emptyFC });
      map.addLayer({
        id: `${SOURCE_PREFIX}-circle-layer`,
        type: "fill",
        source: `${SOURCE_PREFIX}-circle`,
        paint: { "fill-color": "#1a237e", "fill-opacity": 0.06, "fill-outline-color": "#1a237e" },
      });
      // Route line — vehicle path within the verification window, near the pickup.
      map.addSource(`${SOURCE_PREFIX}-line`, { type: "geojson", data: emptyFC });
      map.addLayer({
        id: `${SOURCE_PREFIX}-line-layer`,
        type: "line",
        source: `${SOURCE_PREFIX}-line`,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#888", "line-width": 6 },
      });
      // Arrived-at-Pickup marker (blue). Added BEFORE the pickup pin so when the
      // two coincide the red pickup pin is drawn on top and both are visible.
      // Spec says no number label, so we leave text-field off.
      map.addSource(`${SOURCE_PREFIX}-arrived`, { type: "geojson", data: emptyFC });
      map.addLayer({
        id: `${SOURCE_PREFIX}-arrived-layer`,
        type: "symbol",
        source: `${SOURCE_PREFIX}-arrived`,
        layout: { "icon-image": "otp-marker", "icon-allow-overlap": true, "icon-anchor": "bottom" },
      });
      map.addSource(`${SOURCE_PREFIX}-pickup-pin`, { type: "geojson", data: emptyFC });
      map.addLayer({
        id: `${SOURCE_PREFIX}-pickup-pin-layer`,
        type: "symbol",
        source: `${SOURCE_PREFIX}-pickup-pin`,
        layout: { "icon-image": "otp-pin", "icon-allow-overlap": true, "icon-anchor": "bottom", "icon-size": 0.8 },
      });
      map.addSource(`${SOURCE_PREFIX}-headings`, { type: "geojson", data: emptyFC });
      map.addLayer({
        id: `${SOURCE_PREFIX}-headings-layer`,
        type: "symbol",
        source: `${SOURCE_PREFIX}-headings`,
        minzoom: 8,
        layout: {
          "icon-image": "otp-chevron",
          "icon-allow-overlap": true,
          "icon-rotate": ["get", "heading"],
          // Scale with zoom: ~0.35 when zoomed out, doubling to ~0.7 as you zoom in.
          "icon-size": ["interpolate", ["linear"], ["zoom"], 12, 0.35, 15, 0.7],
        },
        paint: { "icon-color": "#0d1b6e" },
      });
      setMapLoaded(true);
    });
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Hover and pinned popups live as separate instances so they don't interfere.
  // Hover: transient, dark, recreated each mouseenter. Pinned: persistent, light,
  // survives until the user clicks the X. Clicking another marker creates an
  // additional pinned popup — they don't replace each other.
  const hoverPopup = useRef<mapboxgl.Popup | null>(null);
  const pinnedPopups = useRef<Set<mapboxgl.Popup>>(new Set());

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const map = mapRef.current;

    const buildPopupHtml = (props: any, coords: number[]) => {
      const title = props.title || props.type || "Point";
      const time = props.time ? moment.utc(props.time).tz(GetTimeZoneFromAbbreviation(tz)).format("MM/DD/YYYY HH:mm") : null;
      const lat = props.latitude ?? coords[1];
      const lng = props.longitude ?? coords[0];
      return (
        `<div class="fw-bold">${title}</div>` +
        (time ? `<div>${time} ${tz}</div>` : "") +
        `<div>Lat: ${Number(lat).toFixed(5)}</div>` +
        `<div>Long: ${Number(lng).toFixed(5)}</div>`
      );
    };

    const onEnter = (e: any) => {
      map.getCanvas().style.cursor = "pointer";
      const f = e.features?.[0];
      if (!f) return;
      const raw = (f.geometry as any).coordinates;
      let coords: number[] = Array.isArray(raw[0]) ? [e.lngLat.lng, e.lngLat.lat] : raw.slice();
      while (Math.abs(e.lngLat.lng - coords[0]) > 180) {
        coords[0] += e.lngLat.lng > coords[0] ? 360 : -360;
      }
      const html = buildPopupHtml(f.properties || {}, coords);
      if (!hoverPopup.current) {
        hoverPopup.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, className: "vh-popup-dark" });
      }
      hoverPopup.current.setLngLat([coords[0], coords[1]] as [number, number]).setHTML(html).addTo(map);
    };

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = "";
      hoverPopup.current?.remove();
    };

    const onLayerClick = (e: any) => {
      const f = e.features?.[0];
      if (!f) return;
      const raw = (f.geometry as any).coordinates;
      let coords: number[] = Array.isArray(raw[0]) ? [e.lngLat.lng, e.lngLat.lat] : raw.slice();
      while (Math.abs(e.lngLat.lng - coords[0]) > 180) {
        coords[0] += e.lngLat.lng > coords[0] ? 360 : -360;
      }
      const html = buildPopupHtml(f.properties || {}, coords);
      const pinned = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        className: "vh-popup-dark vh-popup-pinned",
      });
      pinned.on("close", () => { pinnedPopups.current.delete(pinned); });
      pinned.setLngLat([coords[0], coords[1]] as [number, number]).setHTML(html).addTo(map);
      pinnedPopups.current.add(pinned);
      // Stop the transient hover popup from overlapping the freshly pinned one.
      hoverPopup.current?.remove();
    };

    // Hover popups only for headings — the pickup pin and arrived-at-pickup marker
    // each render a persistent white popup of their own (see effect below), so
    // hovering them shouldn't add a second tooltip on top.
    const layers = [
      `${SOURCE_PREFIX}-headings-layer`,
    ];
    layers.forEach((l) => {
      map.on("mouseenter", l, onEnter);
      map.on("mouseleave", l, onMouseLeave);
      map.on("click", l, onLayerClick);
    });
    return () => {
      layers.forEach((l) => {
        map.off("mouseenter", l, onEnter);
        map.off("mouseleave", l, onMouseLeave);
        map.off("click", l, onLayerClick);
      });
    };
  }, [mapLoaded, tz]);

  // Compute a 1500m-diameter (750m radius) circle polygon centered on the pickup.
  // Hand-rolled instead of pulling in another dep; degree-per-meter approximation.
  const buildCirclePolygon = (lat: number, lng: number, radiusM: number) => {
    const points = 64;
    const coords: number[][] = [];
    const earth = 6378137;
    const dLat = (radiusM / earth) * (180 / Math.PI);
    const dLng = dLat / Math.cos((lat * Math.PI) / 180);
    for (let i = 0; i <= points; i++) {
      const t = (i / points) * Math.PI * 2;
      coords.push([lng + dLng * Math.cos(t), lat + dLat * Math.sin(t)]);
    }
    return { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [coords] } }] };
  };

  // Haversine distance, meters
  const distM = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const R = 6378137;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  // Push data onto the map whenever inputs change.
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const map = mapRef.current;
    const emptyFC: any = { type: "FeatureCollection", features: [] };
    // Mapbox typings make getSource return AnySourceImpl which doesn't expose setData;
    // every source we create here is a GeoJSONSource, so cast through any.
    const src = (id: string) => map.getSource(id) as any;

    // Pickup pin always stays at the ordered location — the circle moves below.
    if (pickupCoords) {
      src(`${SOURCE_PREFIX}-pickup-pin`)?.setData?.({
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          properties: { title: "Ordered Pickup", latitude: pickupCoords.lat, longitude: pickupCoords.lng },
          geometry: { type: "Point", coordinates: [pickupCoords.lng, pickupCoords.lat] },
        }],
      } as any);
    } else {
      src(`${SOURCE_PREFIX}-pickup-pin`)?.setData?.(emptyFC);
    }

    // Time window: contractual − 1h → driver's Arrived at Pickup (fallback +1h).
    const contractual = (trip as any)?.contractualPickupTime || trip?.pickupDateTimeLocal;
    if (!contractual) return;
    const winStart = moment.utc(contractual).subtract(WINDOW_BEFORE_HOURS, "hours").valueOf();
    const winEnd = arrivedEvent?.time
      ? moment.utc(arrivedEvent.time).valueOf()
      : moment.utc(contractual).add(FALLBACK_AFTER_HOURS, "hours").valueOf();
    const inWindow = (timeStr: any) => {
      if (!timeStr) return false;
      const t = moment.utc(timeStr).valueOf();
      return t >= winStart && t <= winEnd;
    };
    // Driver-recorded Arrived at Pickup event (real, not suggested)
    const arrived = (timeline || []).find((e: any) =>
      e?.type === "Arrived at Pickup" && !e?.isSuggested && e?.isActive !== false && e?.location?.latitude && e?.location?.longitude
    );

    // GPS activity AND the radius circle are centered on where the driver ACTUALLY
    // arrived. Falls back to ordered pickup if no arrival event yet.
    const activityCenterLat = arrived?.location?.latitude ?? pickupCoords?.lat;
    const activityCenterLng = arrived?.location?.longitude ?? pickupCoords?.lng;
    if (activityCenterLat != null && activityCenterLng != null) {
      src(`${SOURCE_PREFIX}-circle`)?.setData?.(
        buildCirclePolygon(activityCenterLat, activityCenterLng, RADIUS_METERS) as any
      );
    } else {
      src(`${SOURCE_PREFIX}-circle`)?.setData?.(emptyFC);
    }
    // If the driver event and the ordered pickup are far enough apart that they
    // wouldn't visually overlap, broaden the GPS-activity filter to cover BOTH
    // locations — the dispatcher needs to see the GPS picture around the ordered
    // pin in that case too. Threshold matches the popup-overlap heuristic below.
    const orderedFarFromArrived =
      pickupCoords && arrived
        ? distM(pickupCoords.lat, pickupCoords.lng, arrived.location.latitude, arrived.location.longitude) > 100
        : false;
    const inRadius = (lat: any, lng: any) => {
      if (lat == null || lng == null) return false;
      const nearArrived =
        activityCenterLat != null && activityCenterLng != null &&
        distM(activityCenterLat, activityCenterLng, lat, lng) <= ACTIVITY_RADIUS_METERS;
      const nearOrdered =
        orderedFarFromArrived && pickupCoords &&
        distM(pickupCoords.lat, pickupCoords.lng, lat, lng) <= ACTIVITY_RADIUS_METERS;
      return nearArrived || nearOrdered;
    };
    if (arrived) {
      src(`${SOURCE_PREFIX}-arrived`)?.setData?.({
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          properties: {
            title: "Arrived at Pickup (driver)",
            time: arrived.time,
            latitude: arrived.location.latitude,
            longitude: arrived.location.longitude,
          },
          geometry: { type: "Point", coordinates: [arrived.location.longitude, arrived.location.latitude] },
        }],
      } as any);
    } else {
      src(`${SOURCE_PREFIX}-arrived`)?.setData?.(emptyFC);
    }

    // Route line within window + radius: connect telemetry points whose coords
    // sit inside the pickup radius. One LineString per contiguous run to avoid
    // crow-flies jumps across out-of-radius gaps.
    const tele = (activity?.telemetry || [])
      .filter((t: any) => inWindow(t.occurrenceDT));
    const runs: number[][][] = [];
    let cur: number[][] = [];
    for (const t of tele) {
      if (inRadius(t.latitude, t.longitude)) {
        cur.push([t.longitude, t.latitude]);
      } else if (cur.length) {
        runs.push(cur);
        cur = [];
      }
    }
    if (cur.length) runs.push(cur);
    const lineFeatures = runs
      .filter((r) => r.length >= 2)
      .map((coords) => ({
        type: "Feature",
        properties: { title: "Route" },
        geometry: { type: "LineString", coordinates: coords },
      }));
    src(`${SOURCE_PREFIX}-line`)?.setData?.({ type: "FeatureCollection", features: lineFeatures } as any);

    // Headings within window + radius
    const headings = (activity?.headings || [])
      .filter((h: any) => inWindow(h.occurrenceDT) && inRadius(h.latitude, h.longitude))
      .map((h: any) => ({
        type: "Feature",
        properties: {
          title: "Heading",
          heading: h.heading ?? 0,
          time: h.occurrenceDT,
          latitude: h.latitude,
          longitude: h.longitude,
        },
        geometry: { type: "Point", coordinates: [h.longitude, h.latitude] },
      }));
    src(`${SOURCE_PREFIX}-headings`)?.setData?.({
      type: "FeatureCollection",
      features: headings,
    } as any);

    // Fit bounds so both the ordered pickup and the driver's arrived event are
    // visible. Single-point fallbacks if only one is known.
    if (pickupCoords && arrived) {
      const bounds = new mapboxgl.LngLatBounds()
        .extend([pickupCoords.lng, pickupCoords.lat])
        .extend([arrived.location.longitude, arrived.location.latitude]);
      map.fitBounds(bounds, { padding: 120, maxZoom: 17, duration: 600 });
    } else if (pickupCoords) {
      map.flyTo({ center: [pickupCoords.lng, pickupCoords.lat], zoom: 15, essential: true });
    }
  }, [mapLoaded, pickupCoords, trip, timeline, activity]);

  // Persistent white popups for the ordered pickup and the driver's Arrived at
  // Pickup event. Always shown (no hover) and positioned above the icons with
  // a small gap so they don't overlap the marker art.
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const map = mapRef.current;
    let pickupPopupHandle: mapboxgl.Popup | null = null;
    let arrivedPopupHandle: mapboxgl.Popup | null = null;

    // Closing a popup via X removes it but keeps the Popup instance alive — we
    // can re-add it on a subsequent marker click. Track per-layer click handlers
    // so we can detach them on cleanup.
    let reopenPickup: (() => void) | null = null;
    let reopenArrived: (() => void) | null = null;

    if (pickupCoords) {
      const requestedTime = trip?.origPickupDateTime
        ? moment(trip.origPickupDateTime).format("MM/DD/YYYY HH:mm")
        : null;
      const pickupName = (trip as any)?.pickupLocation?.name;
      const html =
        `<div class="fw-bold">Ordered Pickup</div>` +
        (pickupName ? `<div>${pickupName}</div>` : "") +
        (requestedTime ? `<div>Requested: ${requestedTime}</div>` : "") +
        `<div>Lat: ${pickupCoords.lat.toFixed(5)}</div>` +
        `<div>Long: ${pickupCoords.lng.toFixed(5)}</div>`;
      pickupPopupHandle = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        className: "vh-popup-dark vh-popup-pinned",
        anchor: "bottom",
        offset: 50, // pin is 40px tall + small gap
      });
      pickupPopupHandle
        .setLngLat([pickupCoords.lng, pickupCoords.lat])
        .setHTML(html)
        .addTo(map);
      reopenPickup = () => {
        if (!pickupPopupHandle?.isOpen()) pickupPopupHandle?.addTo(map);
      };
      map.on("click", `${SOURCE_PREFIX}-pickup-pin-layer`, reopenPickup);
    }

    const arrived = (timeline || []).find((e: any) =>
      e?.type === "Arrived at Pickup" && !e?.isSuggested && e?.isActive !== false && e?.location?.latitude && e?.location?.longitude
    );
    // If the two markers are very close, stack the popups: pickup on top (anchor
    // "bottom" → above the icon), driver below (anchor "top" → below the icon).
    const popupsWouldOverlap = !!(pickupCoords && arrived &&
      distM(pickupCoords.lat, pickupCoords.lng, arrived.location.latitude, arrived.location.longitude) <= 100);
    if (arrived) {
      const arrivedTime = arrived.time
        ? `${moment.utc(arrived.time).tz(GetTimeZoneFromAbbreviation(tz)).format("MM/DD/YYYY HH:mm")} ${tz}`
        : null;
      // Surface the dispatcher-ordered context too so OTP comparison is right here.
      const orderedPickupName = (trip as any)?.pickupLocation?.name;
      const orderedRequestedTime = trip?.origPickupDateTime
        ? moment(trip.origPickupDateTime).format("MM/DD/YYYY HH:mm")
        : null;
      const otpStatus = (trip as any)?.onTimePerformanceStatus;
      const html =
        `<div class="fw-bold">Arrived at Pickup (driver)</div>` +
        (arrivedTime ? `<div>${arrivedTime}</div>` : "") +
        (otpStatus ? `<div>OTP: ${otpStatus}</div>` : "") +
        `<div>Lat: ${Number(arrived.location.latitude).toFixed(5)}</div>` +
        `<div>Long: ${Number(arrived.location.longitude).toFixed(5)}</div>` +
        (orderedPickupName || orderedRequestedTime ? `<div style="margin-top:6px; padding-top:6px; border-top:1px solid rgba(0,0,0,.15);"><div class="fw-bold">Ordered:</div>` +
          (orderedPickupName ? `<div>${orderedPickupName}</div>` : "") +
          (orderedRequestedTime ? `<div>Requested: ${orderedRequestedTime}</div>` : "") +
        `</div>` : "");
      // When overlapping with the pickup popup, anchor "top" so this one hangs
      // below the icon instead of stacking on top of the pickup popup.
      const anchor = popupsWouldOverlap ? "top" : "bottom";
      // For anchor "top", offset moves down from the lngLat. Marker is 32px tall
      // anchored at bottom, so the lngLat is at the marker's tip; a small +offset
      // gives a gap before the popup tip starts.
      const offset = popupsWouldOverlap ? 12 : 42;
      arrivedPopupHandle = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        className: "vh-popup-dark vh-popup-pinned",
        anchor,
        offset,
      });
      arrivedPopupHandle
        .setLngLat([arrived.location.longitude, arrived.location.latitude])
        .setHTML(html)
        .addTo(map);
      reopenArrived = () => {
        if (!arrivedPopupHandle?.isOpen()) arrivedPopupHandle?.addTo(map);
      };
      map.on("click", `${SOURCE_PREFIX}-arrived-layer`, reopenArrived);
    }

    return () => {
      if (reopenPickup) map.off("click", `${SOURCE_PREFIX}-pickup-pin-layer`, reopenPickup);
      if (reopenArrived) map.off("click", `${SOURCE_PREFIX}-arrived-layer`, reopenArrived);
      pickupPopupHandle?.remove();
      arrivedPopupHandle?.remove();
    };
  }, [mapLoaded, pickupCoords, trip, timeline, tz]);

  const loading = !trip || !pickupCoords;

  return (
    <Box sx={{ height: "calc(100vh - 280px)", minHeight: 500, position: "relative" }}>
      {/* Same popup theme classes as the Map tab so OTP and Map look identical:
          hover = dark, click adds vh-popup-pinned to flip to light. */}
      <style>{`
        .vh-popup-dark .mapboxgl-popup-content {
          background: #111;
          color: #fff;
          padding: 8px 10px;
          font-size: 12px;
          line-height: 1.4;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0,0,0,.4);
        }
        .vh-popup-dark .mapboxgl-popup-tip { border-top-color: #111; border-bottom-color: #111; }
        .vh-popup-dark .mapboxgl-popup-close-button { color: #fff; font-size: 18px; padding: 0 6px; }

        .vh-popup-dark.vh-popup-pinned .mapboxgl-popup-content {
          background: #fff;
          color: #111;
          box-shadow: 0 2px 8px rgba(0,0,0,.25);
        }
        .vh-popup-dark.vh-popup-pinned .mapboxgl-popup-tip { border-top-color: #fff; border-bottom-color: #fff; }
        .vh-popup-dark.vh-popup-pinned .mapboxgl-popup-close-button { color: #111; }
      `}</style>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`Pickup area (${RADIUS_METERS * 2}m), contractual − ${WINDOW_BEFORE_HOURS}h → arrival.`}
        </Typography>
      </Box>
      <Box ref={mapContainer} sx={{ position: "absolute", top: 50, left: 0, right: 0, bottom: 0 }} />
      {loading && (
        <Box sx={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: "rgba(255,255,255,0.7)", zIndex: 5,
        }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default OtpVerification;
